import { useRef } from "react";
import type { DatasetField } from "@/api/datasets";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";
import { useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";

/**
 * The table's Columns menu: every field with a check when it shows (a
 * choice keeps the menu open), then Add field.
 */
export function DatasetColumnsMenu({
  fields,
  isShown,
  canToggle,
  onToggle,
  canAdd,
  onAdd,
}: {
  fields: readonly DatasetField[];
  isShown: (id: string) => boolean;
  canToggle: (id: string) => boolean;
  onToggle: (id: string) => void;
  canAdd: boolean;
  onAdd: () => void;
}) {
  const t = useT(framesDatasetMessages);
  // Add field opens a dialog: focus goes there, not back to the trigger.
  const adding = useRef(false);

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) adding.current = false;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="iconSm"
          aria-label={t("columns")}
          title={t("columns")}
          data-testid="dataset-columns"
        >
          <Icons.Columns3 className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] min-w-48 overflow-y-auto"
        onCloseAutoFocus={(event) => adding.current && event.preventDefault()}
      >
        {fields.map((field) => {
          const shown = isShown(field.id);
          return (
            <DropdownMenuItem
              key={field.id}
              role="menuitemcheckbox"
              aria-checked={shown}
              disabled={!canToggle(field.id)}
              // An empty icon slot keeps every name on one edge.
              icon={shown ? <Icons.Check /> : <span />}
              onSelect={(event) => {
                event.preventDefault();
                onToggle(field.id);
              }}
              data-testid={`dataset-column-toggle-${field.id}`}
            >
              {field.name}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<Icons.Plus />}
          disabled={!canAdd}
          onSelect={() => {
            adding.current = true;
            onAdd();
          }}
          data-testid="dataset-add-field"
        >
          {t("addField")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
